import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import styled, { createGlobalStyle } from "styled-components";
import { toast } from "react-toastify";
import { QRCodeSVG } from "qrcode.react";
import { apiClient } from "../../api/client";
import { useAppSelector } from "../../store/hooks";
import { checkRole } from "../recipe_page/recipe_page";
import { useConfirm } from "../../ui/components/ConfirmDialog";

type DeviceDetail = {
  deviceId: string;
  shortId?: string;
  status: string;
  owner?: { username?: string } | string | null;
  name?: string | null;
  panelProfile?: string;
  layoutRevision?: number;
  firmwareVersion?: string | null;
  lastSeen?: string | null;
  registeredAt?: string | null;
  claimedAt?: string | null;
  configRevision?: number;
  configTimezone?: string;
  configSchedulePrefs?: { hourMin: number; hourMax: number } | null;
  configDebugMode?: boolean;
  configWakeIntervalSec?: number | null;
  configFirmwareTrack?: "stable" | "beta" | string;
  pendingOtaVersion?: string | null;
  pendingOtaIssuedAt?: string | null;
  pairingNonceExpiresAt?: string | null;
  hasAuthToken: boolean;
  pendingCommand?: string | null;
  pendingCommandId?: string | null;
  pendingCommandIssuedAt?: string | null;
  renderEventCount?: number;
  nextExpectedWakeAt?: string | null;
  nextExpectedWakeReason?: string | null;
  nextExpectedWakeExplain?: string | null;
  lastRenderedAotdDayKey?: string | null;
  lastRenderedAt?: string | null;
  telemetry?: {
    bootReason?: string;
    wakeReason?: string;
    bootCount?: number;
    freeHeapKb?: number;
    freePsramKb?: number;
    rssiDbm?: number;
    batteryMv?: number;
    lastRenderStatus?: string;
    capturedAt?: string;
  };
  createdAt?: string;
  updatedAt?: string;
};

type RenderEventRow = {
  _id: string;
  date?: string;
  mode?: string;
  byteSize?: number;
  cacheHit?: boolean;
  layoutRevision?: number;
  servedAt?: string;
  ip?: string;
  firmwareVersion?: string;
};

type DeviceLogRow = {
  _id: string;
  receivedAt?: string;
  firmwareVersion?: string;
  bootCount?: number;
  body: string;
  byteSize?: number;
};

type FirmwareReleaseRow = {
  _id: string;
  version: string;
  track: "stable" | "beta" | string;
  url: string;
  sha256: string;
  sizeBytes: number;
  releaseNotes?: string;
  createdAt?: string;
};

type PanelProfileOption = {
  key: string;
  label: string;
  width: number;
  height: number;
  bpp?: number;
  palette?: string;
  nativeOrientation?: string;
};

// Matches the backend fallback in ensureDeviceLayoutFromPreset() —
// n450s_backend/routes/aotdIrlDevice.ts. Kept as a literal (not fetched)
// because it's only used as a select default before the profiles list loads.
const DEFAULT_PANEL_PROFILE_KEY = "gdep133c02-1200x1600";

function formatRelative(date: string | Date | null | undefined): string {
  if (!date) return "—";
  const t = typeof date === "string" ? new Date(date).getTime() : date.getTime();
  if (Number.isNaN(t)) return "—";
  const diff = Date.now() - t;
  if (diff < 0) return "just now";
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(t).toLocaleDateString();
}

function formatAbsolute(date: string | Date | null | undefined): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString();
}

function formatUntil(date: string | Date | null | undefined): string {
  if (!date) return "—";
  const t = typeof date === "string" ? new Date(date).getTime() : date.getTime();
  if (Number.isNaN(t)) return "—";
  const diff = t - Date.now();
  if (diff <= 0) return "now";
  const s = Math.floor(diff / 1000);
  if (s < 60) return `in ${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `in ${m}m`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (h < 24) return mm > 0 ? `in ${h}h ${mm}m` : `in ${h}h`;
  const days = Math.floor(h / 24);
  const hh = h % 24;
  return hh > 0 ? `in ${days}d ${hh}h` : `in ${days}d`;
}

function formatUtcTime(date: string | Date | null | undefined): string {
  if (!date) return "—";
  const d = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return "—";
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return `${hh}:${mm} UTC`;
}

function formatBytes(n?: number): string {
  if (n == null || Number.isNaN(n)) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function extractErrorMessage(e: unknown): string {
  if (e && typeof e === "object") {
    const ax = e as { response?: { data?: { error?: string } }; message?: string };
    const fromBody = ax.response?.data?.error;
    if (typeof fromBody === "string" && fromBody) return fromBody;
    if (typeof ax.message === "string") return ax.message;
  }
  return "request failed";
}

function formatOwner(owner: DeviceDetail["owner"]): string {
  if (!owner) return "unclaimed";
  if (typeof owner === "string") return owner.startsWith("@") ? owner : `@${owner}`;
  const u = owner.username;
  if (!u) return "unclaimed";
  return u.startsWith("@") ? u : `@${u}`;
}

function ownerForConfirm(owner: DeviceDetail["owner"]): string {
  if (!owner) return "nobody";
  if (typeof owner === "string") return owner;
  return owner.username ?? "nobody";
}

function isOnlineDevice(d: DeviceDetail): boolean {
  return !!(d.lastSeen && Date.now() - new Date(d.lastSeen).getTime() < 5 * 60 * 1000);
}

const ClaimPrintStyles = createGlobalStyle`
  @media print {
    body * {
      visibility: hidden;
    }
    .printable-claim,
    .printable-claim * {
      visibility: visible;
    }
    .printable-claim {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
    }
  }
`;

const Wrap = styled.div`
  min-height: calc(100vh - 64px);
  background: #f0f0f0;
  padding: 1.25rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New",
    monospace;
  @media (max-width: 700px) {
    padding: 12px 8px;
  }
`;

const Inner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #232323;
  box-shadow: 6px 6px 0 #232323;
  padding: 1.25rem;
  @media (max-width: 700px) {
    padding: 1rem 0.85rem;
  }
`;

const BackLink = styled(Link)`
  align-self: flex-start;
  font-size: 0.85rem;
  font-weight: 700;
  color: #232323;
  text-decoration: underline;
  &:hover {
    color: #000;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
  @media (max-width: 700px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  flex: 1;
  min-width: 200px;
  @media (max-width: 700px) {
    min-width: 0;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.35rem;
  font-size: 0.8rem;
  @media (max-width: 700px) {
    align-items: flex-start;
  }
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-size: 1rem;
  font-weight: 800;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: #444;
`;

const Mono = styled.span`
  font-size: 0.72rem;
  word-break: break-all;
`;

const ShortBig = styled.span`
  font-size: 1.75rem;
  font-weight: 800;
  text-transform: uppercase;
  @media (max-width: 700px) {
    font-size: 1.35rem;
  }
`;

const StatusPill = styled.span<{ $registered: boolean }>`
  display: inline-block;
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  border: 1px solid #232323;
  background: ${(p) => (p.$registered ? "#dcfce7" : "#e5e7eb")};
  color: #232323;
`;

const OnlineDot = styled.span<{ $online: boolean }>`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(p) => (p.$online ? "#22c55e" : "#9ca3af")};
  border: 1px solid #232323;
`;

const LiveDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  border: 1px solid #232323;
`;

const LivePill = styled.span`
  font-size: 0.7rem;
  color: #555;
`;

const BtnLink = styled(Link)`
  display: inline-block;
  padding: 0.28rem 0.55rem;
  border: 1px solid #232323;
  background: #fff;
  color: #232323;
  text-decoration: none;
  font-weight: 700;
  font-size: 0.72rem;
  box-shadow: 2px 2px #232323;
  &:hover {
    background: #e8e8e8;
  }
`;

const PrimaryBtn = styled.button`
  padding: 0.45rem 0.85rem;
  border: 1px solid #232323;
  background: #ffd24a;
  color: #232323;
  font-family: inherit;
  font-weight: 800;
  font-size: 0.8rem;
  cursor: pointer;
  box-shadow: 3px 3px 0 #232323;
  &:hover:not(:disabled) {
    background: #ffdf73;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const RevokeBtn = styled.button`
  padding: 0.28rem 0.55rem;
  border: 1px solid crimson;
  background: #fff;
  color: #232323;
  font-family: inherit;
  font-weight: 700;
  font-size: 0.72rem;
  cursor: pointer;
  box-shadow: 2px 2px #232323;
  &:hover:not(:disabled) {
    background: #ffe4e4;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DestructiveBtn = styled.button`
  padding: 0.45rem 0.85rem;
  border: 1px solid #232323;
  background: #fca5a5;
  color: #232323;
  font-family: inherit;
  font-weight: 800;
  font-size: 0.8rem;
  cursor: pointer;
  box-shadow: 3px 3px 0 #232323;
  &:hover:not(:disabled) {
    background: #f87171;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const SubtleBtn = styled.button`
  padding: 0.28rem 0.55rem;
  border: 1px solid #232323;
  background: #fff;
  font-family: inherit;
  font-weight: 700;
  font-size: 0.72rem;
  cursor: pointer;
  box-shadow: 2px 2px #232323;
  &:hover:not(:disabled) {
    background: #e8e8e8;
  }
`;

const IdentityGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.65rem 1.25rem;
  font-size: 0.8rem;
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.div`
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #555;
`;

const Value = styled.div`
  word-break: break-word;
`;

const HelpText = styled.p`
  margin: 0.75rem 0 0;
  font-size: 0.65rem;
  color: #666;
  line-height: 1.4;
`;

const TokenPanel = styled.div`
  margin-top: 0.75rem;
  padding: 0.75rem;
  border: 1px solid #232323;
  background: #fafafa;
  font-size: 0.78rem;
`;

const Warn = styled.p`
  margin: 0.35rem 0 0;
  font-size: 0.72rem;
  color: #b45309;
  font-weight: 700;
`;

const InlineMono = styled.code`
  display: block;
  margin-top: 0.35rem;
  padding: 0.35rem;
  background: #fff;
  border: 1px solid #ccc;
  word-break: break-word;
  font-size: 0.72rem;
  max-width: 100%;
  overflow-x: auto;
`;

const CommandRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.75rem;
  @media (max-width: 700px) {
    flex-direction: column;
    align-items: stretch;
    > button {
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
    }
  }
`;

const SelectCmd = styled.select`
  padding: 0.35rem 0.5rem;
  border: 1px solid #232323;
  font-family: inherit;
  font-size: 0.8rem;
  background: #fff;
  box-shadow: 2px 2px 0 #232323;
  box-sizing: border-box;
  @media (max-width: 700px) {
    width: 100%;
    max-width: 100%;
  }
`;

const ConfigInput = styled.input`
  background: #1a1a1a;
  border: 1px solid #333;
  color: #e0e0e0;
  padding: 0.4rem 0.6rem;
  font-family: inherit;
  border-radius: 3px;
  box-sizing: border-box;
  @media (max-width: 700px) {
    width: 100%;
    max-width: 100%;
  }
`;

const ConfigSelect = styled.select`
  background: #1a1a1a;
  border: 1px solid #333;
  color: #e0e0e0;
  padding: 0.4rem 0.6rem;
  font-family: inherit;
  border-radius: 3px;
  box-sizing: border-box;
  @media (max-width: 700px) {
    width: 100%;
    max-width: 100%;
  }
`;

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  @media (max-width: 700px) {
    margin: 0 -8px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.78rem;
  th,
  td {
    border: 1px solid #232323;
    padding: 0.4rem 0.45rem;
    text-align: left;
    vertical-align: middle;
  }
  th {
    background: #fafafa;
    font-weight: 700;
  }
  tbody tr:nth-child(even) {
    background: #fafafa;
  }
  @media (max-width: 700px) {
    min-width: 600px;
  }
`;

const Err = styled.p`
  color: crimson;
  margin: 0 0 0.75rem;
`;

const SectionBlock = styled.div`
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid #e5e5e5;
  &:first-of-type {
    margin-top: 0;
    padding-top: 0;
    border-top: none;
  }
`;

const SectionHeading = styled.h3`
  margin: 0 0 0.5rem;
  font-size: 0.85rem;
  font-weight: 800;
`;

const ToolRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  @media (max-width: 700px) {
    flex-direction: column;
    align-items: stretch;
    > input,
    > select {
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
    }
  }
`;

const PreviewImg = styled.img`
  display: block;
  width: 100%;
  max-width: 320px;
  height: auto;
  border: 1px solid #232323;
  cursor: zoom-in;
  @media (max-width: 700px) {
    max-width: 100%;
  }
`;

const SetupPreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const SetupThumb = styled.img`
  display: block;
  width: 100%;
  max-width: 100%;
  height: auto;
  border: 1px solid #232323;
  background: #fff;
  cursor: zoom-in;
`;

const LogMetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  color: #888;
  margin-bottom: 4px;
  @media (max-width: 700px) {
    flex-direction: column;
    gap: 4px;
  }
`;

const LogPre = styled.pre`
  margin: 0;
  font-size: 0.75rem;
  color: #cfcfcf;
  white-space: pre-wrap;
  word-break: break-word;
  max-width: 100%;
  max-height: 280px;
  overflow: auto;
`;

const QrWrap = styled.div`
  margin-top: 12px;
  svg {
    max-width: 100%;
    height: auto;
  }
`;

type RemoteCommand = "refresh_now" | "show_diagnostics" | "factory_reset" | "clear_wifi";

// Onboarding + claim PNG previews for the current panel profile. Lives on the
// Manage page (not the layout editor) because it's diagnostic info about the
// device, not a designer surface.
function SetupPreviewPanel({
  deviceId,
  panelProfile,
}: {
  deviceId: string;
  panelProfile?: string;
}) {
  const [onboardingUrl, setOnboardingUrl] = useState<string | null>(null);
  const [claimUrl, setClaimUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!deviceId) return;
    setLoading(true);
    setErr(null);
    try {
      const qs = panelProfile
        ? `?panelProfile=${encodeURIComponent(panelProfile)}`
        : "";
      const [onboardingRes, claimRes] = await Promise.all([
        apiClient.get(`/api/aotd-irl/admin/preview/onboarding${qs}`, {
          responseType: "blob",
        }),
        apiClient.get(
          `/api/aotd-irl/admin/preview/claim${qs ? qs + "&" : "?"}deviceId=${encodeURIComponent(deviceId)}`,
          { responseType: "blob" }
        ),
      ]);
      setOnboardingUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(onboardingRes.data as Blob);
      });
      setClaimUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(claimRes.data as Blob);
      });
    } catch (e) {
      setErr(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [deviceId, panelProfile]);

  useEffect(
    () => () => {
      if (onboardingUrl) URL.revokeObjectURL(onboardingUrl);
      if (claimUrl) URL.revokeObjectURL(claimUrl);
    },
    [onboardingUrl, claimUrl]
  );

  return (
    <div>
      <SetupPreviewGrid>
        <div>
          <div style={{ fontSize: 12, color: "#555", marginBottom: 4 }}>onboarding / wifi</div>
          {onboardingUrl ? (
            <SetupThumb
              src={onboardingUrl}
              alt="onboarding preview"
              onClick={() => window.open(onboardingUrl, "_blank")}
            />
          ) : (
            <div
              style={{
                aspectRatio: "3 / 4",
                background: "#f0f0f0",
                border: "1px dashed #bbb",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#999",
                fontSize: 12,
              }}
            >
              click refresh →
            </div>
          )}
        </div>
        <div>
          <div style={{ fontSize: 12, color: "#555", marginBottom: 4 }}>claim qr</div>
          {claimUrl ? (
            <SetupThumb
              src={claimUrl}
              alt="claim preview"
              onClick={() => window.open(claimUrl, "_blank")}
            />
          ) : (
            <div
              style={{
                aspectRatio: "3 / 4",
                background: "#f0f0f0",
                border: "1px dashed #bbb",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#999",
                fontSize: 12,
              }}
            >
              click refresh →
            </div>
          )}
        </div>
      </SetupPreviewGrid>
      {err && (
        <div style={{ color: "#a30000", fontSize: 12, marginTop: 8 }}>
          setup previews failed: {err}
        </div>
      )}
      <div style={{ marginTop: 8 }}>
        <PrimaryBtn type="button" onClick={refresh} disabled={loading}>
          {loading ? "loading…" : "Refresh setup previews"}
        </PrimaryBtn>
      </div>
    </div>
  );
}

export default function AotdIrlAdminDeviceDetail() {
  const auth = useAppSelector((s) => s.auth);
  const { deviceId: rawDeviceId } = useParams<{ deviceId: string }>();
  const deviceId = rawDeviceId ? decodeURIComponent(rawDeviceId) : "";
  const confirm = useConfirm();

  const [device, setDevice] = useState<DeviceDetail | null>(null);
  const [events, setEvents] = useState<RenderEventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  const firstFetchRef = useRef(true);

  const [previewKey, setPreviewKey] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewBlobRef = useRef<string | null>(null);
  const [issuedToken, setIssuedToken] = useState<string | null>(null);
  const [claimUrlPanel, setClaimUrlPanel] = useState<string | null>(null);
  const [selectedCommand, setSelectedCommand] = useState<RemoteCommand>("refresh_now");
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [wakeIntervalDraft, setWakeIntervalDraft] = useState<string>("");
  const [logs, setLogs] = useState<DeviceLogRow[]>([]);
  const [logsOpen, setLogsOpen] = useState<boolean>(false);
  const [releases, setReleases] = useState<FirmwareReleaseRow[]>([]);
  const [otaTargetVersion, setOtaTargetVersion] = useState<string>("");
  const [profiles, setProfiles] = useState<PanelProfileOption[]>([]);
  const [enrollProfileKey, setEnrollProfileKey] = useState<string>("");
  const [changeProfileKey, setChangeProfileKey] = useState<string>("");

  const loadAll = useCallback(async () => {
    if (!deviceId) {
      setLoading(false);
      return;
    }
    try {
      const [devRes, evRes] = await Promise.all([
        apiClient.get<{ device: DeviceDetail }>(
          `/api/aotd-irl/admin/devices/${encodeURIComponent(deviceId)}`
        ),
        apiClient.get<{ events?: RenderEventRow[] }>(
          `/api/aotd-irl/devices/${encodeURIComponent(deviceId)}/render-events?limit=20`
        ),
      ]);
      setDevice(devRes.data.device);
      setEvents(evRes.data?.events ?? []);
      setLastUpdatedAt(Date.now());
      setError(null);
    } catch (e: unknown) {
      const msg = extractErrorMessage(e);
      if (firstFetchRef.current) {
        setError(msg);
        setDevice(null);
      }
    } finally {
      if (firstFetchRef.current) {
        firstFetchRef.current = false;
        setLoading(false);
      }
    }
    try {
      const r = await apiClient.get(
        `/api/aotd-irl/admin/devices/${encodeURIComponent(deviceId)}/logs?limit=20`
      );
      setLogs(Array.isArray(r.data?.logs) ? r.data.logs : []);
    } catch {
      // logs are best-effort
    }
    try {
      const r = await apiClient.get(`/api/aotd-irl/admin/firmware`);
      setReleases(Array.isArray(r.data?.releases) ? r.data.releases : []);
    } catch {
      // firmware list best-effort
    }
    try {
      const r = await apiClient.get<{ profiles?: PanelProfileOption[] }>(
        "/api/aotd-irl/admin/panel-profiles"
      );
      setProfiles(Array.isArray(r.data?.profiles) ? r.data.profiles : []);
    } catch {
      // panel profile list best-effort
    }
  }, [deviceId]);

  useEffect(() => {
    firstFetchRef.current = true;
    setLoading(true);
    setError(null);
    setDevice(null);
    setEvents([]);
    void loadAll();
  }, [loadAll]);

  useEffect(() => {
    const poll = () => {
      if (document.visibilityState === "visible" && deviceId) void loadAll();
    };
    const id = window.setInterval(poll, 5000);
    const onVis = () => {
      if (document.visibilityState === "visible") void loadAll();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [loadAll, deviceId]);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const secsSinceUpdate = Math.max(0, Math.floor((now - lastUpdatedAt) / 1000));

  // Load preview via apiClient (attaches Bearer auth) and materialize as an
  // object URL — a plain `<img src="/api/.../preview-spectra6.png">` would
  // skip the Authorization header and the endpoint returns 401, which is
  // why the preview was showing a broken-image icon before this fix.
  useEffect(() => {
    if (!deviceId) return;
    let cancelled = false;
    setPreviewLoading(true);
    if (previewBlobRef.current) {
      URL.revokeObjectURL(previewBlobRef.current);
      previewBlobRef.current = null;
    }
    (async () => {
      try {
        const res = await apiClient.get(
          `/api/aotd-irl/devices/${encodeURIComponent(deviceId)}/preview-spectra6.png`,
          { params: { date: "today", t: previewKey }, responseType: "arraybuffer" }
        );
        if (cancelled) return;
        const blob = new Blob([res.data as ArrayBuffer], { type: "image/png" });
        const url = URL.createObjectURL(blob);
        previewBlobRef.current = url;
        setPreviewUrl(url);
      } catch {
        if (!cancelled) setPreviewUrl(null);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (previewBlobRef.current) {
        URL.revokeObjectURL(previewBlobRef.current);
        previewBlobRef.current = null;
      }
    };
  }, [deviceId, previewKey]);

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("copied", { position: toast.POSITION.BOTTOM_LEFT });
    } catch {
      toast.error("copy failed", { position: toast.POSITION.BOTTOM_LEFT });
    }
  };

  const handleRegenerateToken = async () => {
    setBusyAction("regen");
    try {
      const resp = await apiClient.post<{ success?: boolean; issuedToken?: string }>(
        `/api/aotd-irl/admin/devices/${encodeURIComponent(deviceId)}/regenerate-token`
      );
      setIssuedToken(resp.data?.issuedToken ?? null);
      setClaimUrlPanel(null);
      toast.success("token regenerated", { position: toast.POSITION.BOTTOM_LEFT });
      await loadAll();
    } catch (err: unknown) {
      toast.error(extractErrorMessage(err), { position: toast.POSITION.BOTTOM_LEFT });
    } finally {
      setBusyAction(null);
    }
  };

  const handleRevokeToken = async () => {
    const ok = await confirm({
      title: "Revoke device token?",
      message: "The device will TOFU (trust-on-first-use) a new token on its next sync.",
      confirmLabel: "Revoke",
      tone: "danger",
    });
    if (!ok) return;
    setBusyAction("revoke");
    try {
      await apiClient.post(`/api/aotd-irl/admin/devices/${encodeURIComponent(deviceId)}/revoke-token`);
      toast.success("token revoked", { position: toast.POSITION.BOTTOM_LEFT });
      await loadAll();
    } catch (err: unknown) {
      toast.error(extractErrorMessage(err), { position: toast.POSITION.BOTTOM_LEFT });
    } finally {
      setBusyAction(null);
    }
  };

  const handleUnclaim = async () => {
    if (!device) return;
    const o = ownerForConfirm(device.owner);
    const ok = await confirm({
      title: "Unclaim this device?",
      message: `It will no longer belong to ${o}. A new pairing nonce will be issued so the device can be resold or re-claimed.`,
      confirmLabel: "Unclaim",
      tone: "danger",
    });
    if (!ok) return;
    setBusyAction("unclaim");
    try {
      const resp = await apiClient.post<{
        success?: boolean;
        claimUrl?: string;
      }>(`/api/aotd-irl/admin/devices/${encodeURIComponent(deviceId)}/unclaim`, { reissueNonce: true });
      setIssuedToken(null);
      if (resp.data?.claimUrl) setClaimUrlPanel(resp.data.claimUrl);
      toast.success("device unclaimed", { position: toast.POSITION.BOTTOM_LEFT });
      await loadAll();
    } catch (err: unknown) {
      toast.error(extractErrorMessage(err), { position: toast.POSITION.BOTTOM_LEFT });
    } finally {
      setBusyAction(null);
    }
  };

  // Admin-enrollment approval. For devices created while AOTD_IRL_AUTO_ENROLL
  // is off, /sync parks them in `pending_enroll` and the frame shows an
  // "ENROLL ME" QR. Scanning that QR lands us here; one click flips the
  // device to "unregistered" and seeds the panel-profile layout preset.
  const handleAdminEnroll = async () => {
    const panelProfile = enrollProfileKey || device?.panelProfile || DEFAULT_PANEL_PROFILE_KEY;
    setBusyAction("enroll");
    try {
      await apiClient.post(
        `/api/aotd-irl/admin/devices/${encodeURIComponent(deviceId)}/enroll`,
        { panelProfile }
      );
      toast.success("device enrolled — next /sync flows into claim", {
        position: toast.POSITION.BOTTOM_LEFT,
      });
      await loadAll();
    } catch (err: unknown) {
      toast.error(extractErrorMessage(err), { position: toast.POSITION.BOTTOM_LEFT });
    } finally {
      setBusyAction(null);
    }
  };

  // There's no single backend endpoint that cleanly changes panelProfile on an
  // already-enrolled device: PATCH /admin/devices/:deviceId doesn't accept the
  // field at all, and PUT /admin/devices/:deviceId/layout only writes
  // device.panelProfile when it was previously unset (see the `if
  // (!device.panelProfile)` guard in n450s_backend/routes/aotdIrlAdminLayout.ts),
  // so calling it on a device that already has one leaves device.panelProfile
  // and the saved layout's panelProfile mismatched. Instead this composes two
  // existing, already-admin-gated endpoints: drop the current layout, then
  // reuse /enroll (which is safe to call again outside pending_enroll — it only
  // flips status when status is pending_enroll/unregistered) to reassign the
  // profile and reseed a fresh layout from that profile's preset.
  const handleChangePanelProfile = async () => {
    if (!device) return;
    const current = device.panelProfile || DEFAULT_PANEL_PROFILE_KEY;
    const next = changeProfileKey || current;
    if (next === current) {
      toast.info("already on this profile", { position: toast.POSITION.BOTTOM_LEFT });
      return;
    }
    const ok = await confirm({
      title: "Change panel profile?",
      message: `this device's current layout will be deleted and reseeded from ${next}'s default preset. any custom zone edits made in the layout editor will be lost. continue?`,
      confirmLabel: "Change profile",
      tone: "danger",
    });
    if (!ok) return;
    setBusyAction("change-profile");
    try {
      await apiClient.delete(`/api/aotd-irl/admin/devices/${encodeURIComponent(deviceId)}/layout`);
      await apiClient.post(`/api/aotd-irl/admin/devices/${encodeURIComponent(deviceId)}/enroll`, {
        panelProfile: next,
      });
      toast.success(`panel profile changed to ${next}; layout reset to default`, {
        position: toast.POSITION.BOTTOM_LEFT,
      });
      await loadAll();
    } catch (err: unknown) {
      toast.error(extractErrorMessage(err), { position: toast.POSITION.BOTTOM_LEFT });
    } finally {
      setBusyAction(null);
    }
  };

  const handleReissueNonce = async () => {
    setBusyAction("reissue");
    try {
      const resp = await apiClient.post<{ claimUrl?: string; success?: boolean }>(
        `/api/aotd-irl/admin/devices/${encodeURIComponent(deviceId)}/reissue-nonce`
      );
      if (resp.data?.claimUrl) setClaimUrlPanel(resp.data.claimUrl);
      toast.success("claim qr reissued", { position: toast.POSITION.BOTTOM_LEFT });
      await loadAll();
    } catch (err: unknown) {
      toast.error(extractErrorMessage(err), { position: toast.POSITION.BOTTOM_LEFT });
    } finally {
      setBusyAction(null);
    }
  };

  const handleCancelCommand = async () => {
    setBusyAction("cancel-cmd");
    try {
      await apiClient.delete(`/api/aotd-irl/admin/devices/${encodeURIComponent(deviceId)}/command`);
      toast.success("pending command cleared", { position: toast.POSITION.BOTTOM_LEFT });
      await loadAll();
    } catch (err: unknown) {
      toast.error(extractErrorMessage(err), { position: toast.POSITION.BOTTOM_LEFT });
    } finally {
      setBusyAction(null);
    }
  };

  const handleSaveConfig = useCallback(
    async (patch: {
      debugMode?: boolean;
      wakeIntervalSec?: number;
      firmwareTrack?: "stable" | "beta";
    }) => {
      try {
        const res = await apiClient.patch(
          `/api/aotd-irl/admin/devices/${encodeURIComponent(deviceId)}/config`,
          patch
        );
        toast.success(`config updated (rev ${res.data?.configRevision ?? "?"})`, {
          position: toast.POSITION.BOTTOM_LEFT,
        });
        await loadAll();
      } catch (e) {
        toast.error(extractErrorMessage(e), { position: toast.POSITION.BOTTOM_LEFT });
      }
    },
    [deviceId, loadAll]
  );

  const handleAssignOta = useCallback(
    async (version: string) => {
      if (!version) {
        toast.error("pick a firmware version", { position: toast.POSITION.BOTTOM_LEFT });
        return;
      }
      try {
        await apiClient.post(
          `/api/aotd-irl/admin/devices/${encodeURIComponent(deviceId)}/ota`,
          { version }
        );
        toast.success(`ota target set to ${version}`, { position: toast.POSITION.BOTTOM_LEFT });
        await loadAll();
      } catch (e) {
        toast.error(extractErrorMessage(e), { position: toast.POSITION.BOTTOM_LEFT });
      }
    },
    [deviceId, loadAll]
  );

  const handleClearOta = useCallback(async () => {
    try {
      await apiClient.delete(
        `/api/aotd-irl/admin/devices/${encodeURIComponent(deviceId)}/ota`
      );
      toast.success("pending ota cleared", { position: toast.POSITION.BOTTOM_LEFT });
      await loadAll();
    } catch (e) {
      toast.error(extractErrorMessage(e), { position: toast.POSITION.BOTTOM_LEFT });
    }
  }, [deviceId, loadAll]);

  const handleSendCommand = async () => {
    const cmd = selectedCommand;
    if (cmd === "factory_reset" || cmd === "clear_wifi") {
      const ok = await confirm({
        title: cmd === "factory_reset" ? "Send factory_reset?" : "Send clear_wifi?",
        message:
          cmd === "factory_reset"
            ? "This is destructive on the device — it will wipe local config and require re-pairing."
            : "Wi-Fi credentials will be cleared on the device; it will drop to onboarding mode until reconnected.",
        confirmLabel: "Send command",
        tone: "danger",
      });
      if (!ok) return;
    }
    setBusyAction("cmd");
    try {
      await apiClient.post(`/api/aotd-irl/admin/devices/${encodeURIComponent(deviceId)}/command`, {
        command: cmd,
      });
      toast.success(`command queued: ${cmd}`, { position: toast.POSITION.BOTTOM_LEFT });
      await loadAll();
    } catch (err: unknown) {
      toast.error(extractErrorMessage(err), { position: toast.POSITION.BOTTOM_LEFT });
    } finally {
      setBusyAction(null);
    }
  };

  if (auth.bootstrapStatus !== "ready") {
    return null;
  }
  if (!auth.token) {
    return <Navigate to="/login" replace />;
  }
  if (!checkRole(auth.role ?? "", "admin")) {
    return <Navigate to="/" replace />;
  }
  if (!deviceId) {
    return <Navigate to="/aotd-irl/admin" replace />;
  }

  const online = device ? isOnlineDevice(device) : false;
  const reg = device?.status === "registered";

  return (
    <Wrap>
      <ClaimPrintStyles />
      <Inner>
        <Card>
          <HeaderRow>
            <HeaderLeft>
              <BackLink to="/aotd-irl/admin">← aotd-irl admin</BackLink>
              <ShortBig>{(device?.shortId ?? "—").toString().toUpperCase()}</ShortBig>
              <Subtitle style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "0.5rem" }}>
                <span>
                  deviceId: <Mono>{deviceId}</Mono>
                </span>
                {device && (
                  <>
                    <StatusPill $registered={!!reg}>{device.status}</StatusPill>
                    <span
                      style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                      title={formatAbsolute(device.lastSeen)}
                    >
                      <OnlineDot $online={online} title={online ? "online" : "offline"} />
                      <span>
                        last seen {formatRelative(device.lastSeen)}
                        {device.lastSeen && (
                          <span style={{ color: "#888" }}>
                            {" "}
                            ({formatAbsolute(device.lastSeen)})
                          </span>
                        )}
                      </span>
                    </span>
                  </>
                )}
              </Subtitle>
            </HeaderLeft>
            <HeaderRight>
              <BtnLink to={`/aotd-irl/admin/devices/${encodeURIComponent(deviceId)}/layout`}>
                Manage layout
              </BtnLink>
              <LivePill>
                <LiveDot /> · updated {secsSinceUpdate}s ago
              </LivePill>
            </HeaderRight>
          </HeaderRow>
        </Card>

        {loading && <Card>loading…</Card>}
        {error && <Err>{error}</Err>}

        {!loading && !error && device && (
          <>
            <Card>
              <SectionTitle>identity</SectionTitle>
              <IdentityGrid>
                <div>
                  <Label>owner</Label>
                  <Value>{formatOwner(device.owner)}</Value>
                </div>
                <div>
                  <Label>panel profile</Label>
                  <Value>
                    {device.panelProfile ?? "—"} · r{device.layoutRevision ?? 0}
                  </Value>
                  {device.status !== "pending_enroll" && (
                    <ToolRow style={{ marginTop: "0.4rem" }}>
                      <SelectCmd
                        value={changeProfileKey || device.panelProfile || DEFAULT_PANEL_PROFILE_KEY}
                        onChange={(e) => setChangeProfileKey(e.target.value)}
                      >
                        {profiles.length === 0 && (
                          <option value={device.panelProfile || DEFAULT_PANEL_PROFILE_KEY}>
                            {device.panelProfile || DEFAULT_PANEL_PROFILE_KEY}
                          </option>
                        )}
                        {profiles.map((p) => (
                          <option key={p.key} value={p.key}>
                            {p.label} ({p.key})
                          </option>
                        ))}
                      </SelectCmd>
                      <SubtleBtn
                        type="button"
                        disabled={busyAction === "change-profile"}
                        onClick={() => void handleChangePanelProfile()}
                      >
                        Change profile
                      </SubtleBtn>
                    </ToolRow>
                  )}
                </div>
                <div>
                  <Label>firmware</Label>
                  <Value>{device.firmwareVersion ?? "—"}</Value>
                </div>
                <div>
                  <Label>config revision</Label>
                  <Value>{device.configRevision ?? "—"}</Value>
                </div>
                <div>
                  <Label>created</Label>
                  <Value title={formatAbsolute(device.createdAt)}>
                    {formatRelative(device.createdAt)}{" "}
                    <span style={{ color: "#888" }}>({formatAbsolute(device.createdAt) || "—"})</span>
                  </Value>
                </div>
                <div>
                  <Label>updated</Label>
                  <Value title={formatAbsolute(device.updatedAt)}>
                    {formatRelative(device.updatedAt)}{" "}
                    <span style={{ color: "#888" }}>({formatAbsolute(device.updatedAt) || "—"})</span>
                  </Value>
                </div>
                <div>
                  <Label>registered at</Label>
                  <Value title={formatAbsolute(device.registeredAt)}>
                    {device.registeredAt ? formatRelative(device.registeredAt) : "—"}
                  </Value>
                </div>
                <div>
                  <Label>claimed at</Label>
                  <Value title={formatAbsolute(device.claimedAt)}>
                    {device.claimedAt ? formatRelative(device.claimedAt) : "—"}
                  </Value>
                </div>
                <div>
                  <Label>next wake</Label>
                  <Value title={formatAbsolute(device.nextExpectedWakeAt)}>
                    {device.nextExpectedWakeAt
                      ? `${formatUtcTime(device.nextExpectedWakeAt)} (${formatUntil(device.nextExpectedWakeAt)})`
                      : "—"}
                    {device.nextExpectedWakeExplain && (
                      <div style={{ fontSize: "0.72rem", color: "#888", marginTop: 2 }}>
                        {device.nextExpectedWakeExplain}
                      </div>
                    )}
                  </Value>
                </div>
                <div>
                  <Label>last rendered aotd</Label>
                  <Value title={formatAbsolute(device.lastRenderedAt)}>
                    {device.lastRenderedAotdDayKey ?? "—"}
                    {device.lastRenderedAt && (
                      <span style={{ color: "#888" }}>
                        {" "}
                        ({formatRelative(device.lastRenderedAt)})
                      </span>
                    )}
                  </Value>
                </div>
                {!reg && device.pairingNonceExpiresAt && (
                  <div>
                    <Label>pairing nonce expires</Label>
                    <Value title={formatAbsolute(device.pairingNonceExpiresAt)}>
                      {formatRelative(device.pairingNonceExpiresAt)}
                    </Value>
                  </div>
                )}
                <div>
                  <Label>has auth token</Label>
                  <Value>{device.hasAuthToken ? "✓" : "✗"}</Value>
                </div>
                <div>
                  <Label>render events</Label>
                  <Value>{device.renderEventCount ?? 0}</Value>
                </div>
              </IdentityGrid>
            </Card>

            {device.telemetry && (
              <Card>
                <SectionTitle>device telemetry</SectionTitle>
                <IdentityGrid>
                  <div>
                    <Label>boot reason</Label>
                    <Value>{device.telemetry.bootReason ?? "—"}</Value>
                  </div>
                  <div>
                    <Label>wake reason</Label>
                    <Value>{device.telemetry.wakeReason ?? "—"}</Value>
                  </div>
                  <div>
                    <Label>boot count</Label>
                    <Value>{device.telemetry.bootCount ?? "—"}</Value>
                  </div>
                  <div>
                    <Label>free heap</Label>
                    <Value>
                      {device.telemetry.freeHeapKb != null ? `${device.telemetry.freeHeapKb} KB` : "—"}
                    </Value>
                  </div>
                  <div>
                    <Label>free psram</Label>
                    <Value>
                      {device.telemetry.freePsramKb != null ? `${device.telemetry.freePsramKb} KB` : "—"}
                    </Value>
                  </div>
                  <div>
                    <Label>wifi rssi</Label>
                    <Value>
                      {device.telemetry.rssiDbm != null ? `${device.telemetry.rssiDbm} dBm` : "—"}
                    </Value>
                  </div>
                  <div>
                    <Label>battery</Label>
                    <Value>
                      {device.telemetry.batteryMv != null && device.telemetry.batteryMv > 0
                        ? `${(device.telemetry.batteryMv / 1000).toFixed(2)} V`
                        : "—"}
                    </Value>
                  </div>
                  <div>
                    <Label>last render</Label>
                    <Value>{device.telemetry.lastRenderStatus ?? "—"}</Value>
                  </div>
                  <div>
                    <Label>captured</Label>
                    <Value title={formatAbsolute(device.telemetry.capturedAt)}>
                      {formatRelative(device.telemetry.capturedAt)}
                    </Value>
                  </div>
                </IdentityGrid>
              </Card>
            )}

            <Card>
              <SectionTitle>device configuration</SectionTitle>
              <SectionBlock>
                <SectionHeading>debug mode</SectionHeading>
                <ToolRow>
                  <Value>{device.configDebugMode ? "on" : "off"}</Value>
                  <PrimaryBtn
                    type="button"
                    onClick={() => void handleSaveConfig({ debugMode: !device.configDebugMode })}
                  >
                    {device.configDebugMode ? "Disable debug mode" : "Enable debug mode"}
                  </PrimaryBtn>
                </ToolRow>
                <HelpText>
                  When on, the device buffers logs in RAM and ships them to the backend on the next sync.
                  Slightly higher battery cost only while enabled.
                </HelpText>
              </SectionBlock>

              <SectionBlock>
                <SectionHeading>wake interval (seconds)</SectionHeading>
                <ToolRow>
                  <Value>
                    {device.configWakeIntervalSec
                      ? `${device.configWakeIntervalSec}s (override)`
                      : "default (from schedule)"}
                  </Value>
                </ToolRow>
                <ToolRow>
                  <ConfigInput
                    type="number"
                    min={60}
                    max={86400}
                    placeholder="60..86400 or empty"
                    value={wakeIntervalDraft}
                    onChange={(e) => setWakeIntervalDraft(e.target.value)}
                  />
                  <PrimaryBtn
                    type="button"
                    onClick={() => {
                      const n = Number(wakeIntervalDraft);
                      if (!Number.isFinite(n) || n < 60 || n > 86400) {
                        toast.error("wake interval must be 60..86400 seconds", {
                          position: toast.POSITION.BOTTOM_LEFT,
                        });
                        return;
                      }
                      void handleSaveConfig({ wakeIntervalSec: Math.floor(n) });
                    }}
                  >
                    Set
                  </PrimaryBtn>
                </ToolRow>
                <HelpText>
                  Upper bound for how long the device sleeps between syncs. Lower = faster command pickup
                  but more battery drain. Leave default for normal operation.
                </HelpText>
              </SectionBlock>

              <SectionBlock>
                <SectionHeading>firmware track</SectionHeading>
                <ToolRow>
                  <Value>{device.configFirmwareTrack ?? "stable"}</Value>
                  <PrimaryBtn
                    type="button"
                    onClick={() =>
                      void handleSaveConfig({
                        firmwareTrack: device.configFirmwareTrack === "beta" ? "stable" : "beta",
                      })
                    }
                  >
                    Switch to {device.configFirmwareTrack === "beta" ? "stable" : "beta"}
                  </PrimaryBtn>
                </ToolRow>
              </SectionBlock>

              <HelpText style={{ marginTop: "0.75rem" }}>
                Pending changes apply on the device's next sync. Current synced revision:{" "}
                <Mono>{device.configRevision ?? "?"}</Mono>.
              </HelpText>
            </Card>

            <Card>
              <SectionTitle>ota</SectionTitle>
              <SectionBlock>
                <SectionHeading>current</SectionHeading>
                <ToolRow>
                  <Value>{device.firmwareVersion ?? "—"}</Value>
                </ToolRow>
              </SectionBlock>
              {device.pendingOtaVersion ? (
                <SectionBlock>
                  <SectionHeading>pending</SectionHeading>
                  <ToolRow>
                    <Value>
                      target: <Mono>{device.pendingOtaVersion}</Mono>
                      {device.pendingOtaIssuedAt
                        ? ` · queued ${formatRelative(device.pendingOtaIssuedAt)}`
                        : ""}
                    </Value>
                    <PrimaryBtn type="button" onClick={handleClearOta}>
                      Cancel pending OTA
                    </PrimaryBtn>
                  </ToolRow>
                </SectionBlock>
              ) : null}
              <SectionBlock>
                <SectionHeading>deploy version</SectionHeading>
                <ToolRow>
                  <ConfigSelect
                    value={otaTargetVersion}
                    onChange={(e) => setOtaTargetVersion(e.target.value)}
                  >
                    <option value="">— pick a release —</option>
                    {releases.map((r) => (
                      <option key={r._id} value={r.version}>
                        {r.version} ({r.track})
                      </option>
                    ))}
                  </ConfigSelect>
                  <PrimaryBtn
                    type="button"
                    onClick={() => handleAssignOta(otaTargetVersion)}
                  >
                    Deploy
                  </PrimaryBtn>
                  <Link
                    to="/aotd-irl/admin/firmware"
                    style={{ color: "#888", textDecoration: "underline", fontSize: ".8rem" }}
                  >
                    Manage releases →
                  </Link>
                </ToolRow>
              </SectionBlock>
              <HelpText>
                Device picks up the OTA on its next sync. Verifies SHA-256 before flashing.
                Rolls back automatically if the new firmware fails to complete a sync after reboot.
              </HelpText>
            </Card>

            <Card>
              <SectionTitle>device logs</SectionTitle>
              <SectionBlock>
                <ToolRow>
                  <Value>
                    {logs.length} batch{logs.length === 1 ? "" : "es"} ·{" "}
                    {device.configDebugMode ? "debug mode on" : "debug mode off"}
                  </Value>
                  <PrimaryBtn type="button" onClick={() => setLogsOpen((v) => !v)}>
                    {logsOpen ? "Hide" : "Show"}
                  </PrimaryBtn>
                </ToolRow>
              </SectionBlock>
              {logsOpen && (
                <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                  {logs.length === 0 && (
                    <HelpText>
                      No logs yet. Enable debug mode in the configuration card; logs will be uploaded
                      on the device's next sync.
                    </HelpText>
                  )}
                  {logs.map((log) => (
                    <div
                      key={log._id}
                      style={{
                        background: "#0d0d0d",
                        border: "1px solid #232323",
                        borderRadius: 3,
                        padding: "0.5rem 0.6rem",
                      }}
                    >
                      <LogMetaRow>
                        <span>
                          {formatRelative(log.receivedAt)}{" "}
                          <span style={{ color: "#555" }}>
                            ({formatAbsolute(log.receivedAt)})
                          </span>
                        </span>
                        <span>
                          fw <Mono>{log.firmwareVersion ?? "—"}</Mono> · boot #{log.bootCount ?? "?"}
                        </span>
                      </LogMetaRow>
                      <LogPre>{log.body}</LogPre>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card>
              <SectionTitle>preview</SectionTitle>
              {previewLoading ? (
                <div style={{ padding: "1rem", color: "#666" }}>rendering…</div>
              ) : previewUrl ? (
                <PreviewImg
                  src={previewUrl}
                  alt="device preview"
                  onClick={() => window.open(previewUrl, "_blank")}
                />
              ) : (
                <div style={{ padding: "1rem", color: "#b45309" }}>
                  preview unavailable — click refresh or check that the device has been
                  enrolled + has a layout.
                </div>
              )}
              <div style={{ marginTop: 8 }}>
                <PrimaryBtn type="button" onClick={() => setPreviewKey(Date.now())}>
                  Refresh preview
                </PrimaryBtn>
              </div>
            </Card>

            <Card>
              <SectionTitle>setup screens</SectionTitle>
              <Subtitle style={{ marginBottom: 8 }}>
                onboarding (wifi) and claim qr, rendered for this device&apos;s panel profile
              </Subtitle>
              <SetupPreviewPanel
                deviceId={deviceId}
                panelProfile={device?.panelProfile}
              />
            </Card>

            <Card>
              <SectionTitle>reset & commands</SectionTitle>

              <SectionBlock>
                <SectionHeading>token</SectionHeading>
                <ToolRow>
                  <PrimaryBtn
                    type="button"
                    disabled={busyAction === "regen"}
                    onClick={() => void handleRegenerateToken()}
                  >
                    Regenerate token
                  </PrimaryBtn>
                  <RevokeBtn
                    type="button"
                    disabled={busyAction === "revoke"}
                    onClick={() => void handleRevokeToken()}
                  >
                    Revoke token
                  </RevokeBtn>
                </ToolRow>
                {issuedToken && (
                  <TokenPanel>
                    <strong>one-time token</strong>
                    <Warn>this is shown once; copy now</Warn>
                    <InlineMono>{issuedToken}</InlineMono>
                    <PrimaryBtn
                      type="button"
                      style={{ marginTop: 8 }}
                      onClick={() => void handleCopy(issuedToken)}
                    >
                      Copy token
                    </PrimaryBtn>
                  </TokenPanel>
                )}
              </SectionBlock>

              <SectionBlock>
                <SectionHeading>pairing</SectionHeading>
                {device.status === "pending_enroll" ? (
                  <>
                    <p style={{ fontSize: "0.85rem", color: "#555", marginBottom: "0.65rem" }}>
                      this frame is waiting for admin approval (auto-enroll is off).
                      pick the panel hardware, then click enroll to flip its status
                      and seed that profile's layout preset; the next /sync on the
                      frame will surface the usual claim QR for the owner.
                    </p>
                    <ToolRow>
                      <SelectCmd
                        value={enrollProfileKey || device.panelProfile || DEFAULT_PANEL_PROFILE_KEY}
                        onChange={(e) => setEnrollProfileKey(e.target.value)}
                      >
                        {profiles.length === 0 && (
                          <option value={device.panelProfile || DEFAULT_PANEL_PROFILE_KEY}>
                            {device.panelProfile || DEFAULT_PANEL_PROFILE_KEY}
                          </option>
                        )}
                        {profiles.map((p) => (
                          <option key={p.key} value={p.key}>
                            {p.label} ({p.key})
                          </option>
                        ))}
                      </SelectCmd>
                      <PrimaryBtn
                        type="button"
                        disabled={busyAction === "enroll"}
                        onClick={() => void handleAdminEnroll()}
                      >
                        Enroll this frame
                      </PrimaryBtn>
                    </ToolRow>
                  </>
                ) : reg ? (
                  <DestructiveBtn
                    type="button"
                    disabled={busyAction === "unclaim"}
                    onClick={() => void handleUnclaim()}
                  >
                    Unclaim & reissue claim QR
                  </DestructiveBtn>
                ) : (
                  <ToolRow>
                    <PrimaryBtn
                      type="button"
                      disabled={busyAction === "reissue"}
                      onClick={() => void handleReissueNonce()}
                    >
                      Reissue claim QR
                    </PrimaryBtn>
                  </ToolRow>
                )}
                {claimUrlPanel && (
                  <TokenPanel className="printable-claim">
                    <strong>claim url</strong>
                    <InlineMono>{claimUrlPanel}</InlineMono>
                    <ToolRow style={{ marginTop: 8 }}>
                      <PrimaryBtn type="button" onClick={() => void handleCopy(claimUrlPanel)}>
                        Copy URL
                      </PrimaryBtn>
                      <SubtleBtn type="button" onClick={() => window.print()}>
                        Print QR
                      </SubtleBtn>
                    </ToolRow>
                    <QrWrap>
                      <QRCodeSVG value={claimUrlPanel} size={140} level="M" />
                    </QrWrap>
                  </TokenPanel>
                )}
              </SectionBlock>

              <SectionBlock>
                <SectionHeading>remote commands</SectionHeading>
                {device.pendingCommand && (
                  <div style={{ fontSize: "0.8rem", marginBottom: 8 }}>
                    Pending: {device.pendingCommand}
                    {device.pendingCommandIssuedAt
                      ? ` (queued ${formatRelative(device.pendingCommandIssuedAt)})`
                      : ""}{" "}
                    <SubtleBtn
                      type="button"
                      disabled={busyAction === "cancel-cmd"}
                      onClick={() => void handleCancelCommand()}
                    >
                      Cancel
                    </SubtleBtn>
                    {device.nextExpectedWakeAt && (
                      <div style={{ fontSize: "0.72rem", color: "#888", marginTop: 2 }}>
                        will be delivered on the next sync (~{formatUtcTime(device.nextExpectedWakeAt)},{" "}
                        {formatUntil(device.nextExpectedWakeAt)}). Lower wakeIntervalSec below{" "}
                        {device.configWakeIntervalSec ?? 3600}s to speed this up.
                      </div>
                    )}
                  </div>
                )}
                <CommandRow>
                  <SelectCmd
                    value={selectedCommand}
                    onChange={(e) => setSelectedCommand(e.target.value as RemoteCommand)}
                  >
                    <option value="refresh_now">refresh_now</option>
                    <option value="show_diagnostics">show_diagnostics</option>
                    <option value="factory_reset">factory_reset</option>
                    <option value="clear_wifi">clear_wifi</option>
                  </SelectCmd>
                  <PrimaryBtn
                    type="button"
                    disabled={busyAction === "cmd"}
                    onClick={() => void handleSendCommand()}
                  >
                    Send command
                  </PrimaryBtn>
                </CommandRow>
                <HelpText>
                  the device picks up commands on its next sync
                  {device.nextExpectedWakeAt
                    ? ` (~${formatUtcTime(device.nextExpectedWakeAt)}, ${formatUntil(device.nextExpectedWakeAt)})`
                    : ""}
                  . toggle debugMode + wakeIntervalSec=60 to poll every minute while testing.
                </HelpText>
              </SectionBlock>
            </Card>

            <Card>
              <SectionTitle>recent activity</SectionTitle>
              {events.length === 0 ? (
                <p style={{ margin: 0, fontSize: "0.85rem" }}>No render events recorded yet</p>
              ) : (
                <TableWrap>
                  <Table>
                    <thead>
                      <tr>
                        <th>Served at</th>
                        <th>Date</th>
                        <th>Mode</th>
                        <th>Cache hit</th>
                        <th>Layout rev</th>
                        <th>Bytes</th>
                        <th>Firmware</th>
                        <th>IP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.map((ev) => (
                        <tr key={String(ev._id)}>
                          <td title={formatAbsolute(ev.servedAt)}>
                            {formatRelative(ev.servedAt)}
                            {ev.servedAt && (
                              <div style={{ color: "#888", fontSize: "0.7rem" }}>
                                {formatAbsolute(ev.servedAt)}
                              </div>
                            )}
                          </td>
                          <td title={ev.date ? formatAbsolute(ev.date) : undefined}>
                          {ev.date ? new Date(ev.date as string).toLocaleDateString() : "—"}
                        </td>
                          <td>{ev.mode ?? "—"}</td>
                          <td>{ev.cacheHit === true ? "yes" : ev.cacheHit === false ? "no" : "—"}</td>
                          <td>{ev.layoutRevision ?? "—"}</td>
                          <td>{formatBytes(ev.byteSize)}</td>
                          <td>{ev.firmwareVersion ?? "—"}</td>
                          <td>{ev.ip ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrap>
              )}
            </Card>
          </>
        )}
      </Inner>
    </Wrap>
  );
}
